import React, { useState, useEffect, useCallback, useRef } from 'react';
import { accountingAPI, contactsAPI } from '../../../services/api';
import { PageHeader } from '../../../components/layout/PageHeader';
import { ArrowDownTrayIcon, PrinterIcon, MagnifyingGlassIcon, UserIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const fmt  = n => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
const fmtD = d => { try { return format(new Date(d), 'dd/MM/yyyy'); } catch { return '—'; } };

const TYPE_LABEL = {
  sale: 'Sale', purchase: 'Purchase', expense: 'Expense',
  indirect_income: 'Income', quotation: 'Quotation', proforma: 'Proforma',
  delivery_challan: 'Delivery Challan', debit_note: 'Debit Note',
  payment_in: 'Payment-In', payment_out: 'Payment-Out',
};

function LedgerTable({ records, openingBalance = 0, contact }) {
  // Compute running balance like the PDF
  let balance = openingBalance;
  const rows = records.map(r => {
    const isCredit = ['sale', 'indirect_income', 'proforma'].includes(r.type);
    const isDebit  = ['purchase', 'expense', 'debit_note'].includes(r.type);
    const isPmtIn  = r.type === 'payment_in'  || r.txnType === 'payment_in';
    const isPmtOut = r.type === 'payment_out' || r.txnType === 'payment_out';

    let receivedPaid = 0;
    let txnBalance   = 0;

    if (isPmtIn) {
      receivedPaid = r.total || r.paidAmount || 0;
      balance -= receivedPaid;
    } else if (isPmtOut) {
      receivedPaid = r.total || r.paidAmount || 0;
      balance += receivedPaid;
    } else {
      txnBalance = r.total || 0;
      balance += txnBalance;
    }

    return { ...r, receivedPaid, txnBalance, runningBalance: balance };
  });

  const totalReceivable = Math.max(balance, 0);
  const totalPayable    = Math.max(-balance, 0);

  return (
    <div>
      {/* Header like PDF */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm print:shadow-none print:border-gray-300">
        {/* Company Header */}
        <div className="bg-indigo-600 text-white px-6 py-5 print:bg-gray-800">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-bold">DP Enterprise</h2>
              <p className="text-indigo-200 text-xs mt-0.5">Party Statement / Ledger</p>
            </div>
            <div className="text-right text-xs text-indigo-200">
              <p>GSTIN: 24AAZFD0433F1ZJ</p>
              <p>State: 24-Gujarat</p>
            </div>
          </div>
        </div>

        {/* Party Info */}
        {contact && (
          <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-0.5">Party Name</p>
                <p className="font-bold text-gray-900">{contact.name}</p>
                {contact.gstin && <p className="text-xs text-gray-500 mt-0.5">GSTIN: {contact.gstin}</p>}
              </div>
              <div className="text-right">
                {contact.phone && <p className="text-xs text-gray-600">📞 {contact.phone}</p>}
                {contact.address && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {typeof contact.address === 'object'
                      ? [contact.address.street, contact.address.city, contact.address.state, contact.address.postalCode, contact.address.country].filter(Boolean).join(', ')
                      : contact.address}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-900 text-white text-xs">
                {['Date','Txn Type','Ref No.','Total','Received / Paid','Txn Balance','Receivable','Payable','Balance'].map(h => (
                  <th key={h} className="px-3 py-3 text-right first:text-left font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400 text-sm">No transactions found for this period</td></tr>
              )}
              {rows.map((r, i) => {
                const isPmt = r.receivedPaid > 0;
                const bal   = r.runningBalance;
                return (
                  <tr key={r._id || i} className={`hover:bg-gray-50 text-xs ${isPmt ? 'bg-green-50/40' : ''}`}>
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{fmtD(r.date)}</td>
                    <td className="px-3 py-2.5 font-medium text-gray-800 whitespace-nowrap">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                        isPmt ? 'bg-green-100 text-green-700' : 'bg-indigo-50 text-indigo-700'
                      }`}>
                        {isPmt ? (r.type === 'payment_out' ? 'Payment-Out' : 'Payment-In') : (TYPE_LABEL[r.type] || r.type)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-gray-500 text-right">{r.invoiceNumber || r.refNo || '—'}</td>
                    <td className="px-3 py-2.5 text-right font-medium text-gray-800">{r.total ? fmt(r.total) : '—'}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-green-700">{isPmt ? fmt(r.receivedPaid) : '—'}</td>
                    <td className="px-3 py-2.5 text-right text-gray-600">{!isPmt && r.txnBalance ? fmt(r.txnBalance) : '—'}</td>
                    <td className="px-3 py-2.5 text-right text-gray-700">{bal > 0 ? fmt(bal) : '—'}</td>
                    <td className="px-3 py-2.5 text-right text-gray-700">{bal < 0 ? fmt(Math.abs(bal)) : '—'}</td>
                    <td className="px-3 py-2.5 text-right font-bold text-gray-900">{fmt(Math.abs(bal))}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-gray-900 text-white font-bold text-xs">
                <td colSpan={6} className="px-3 py-3 text-right">Closing Balance:</td>
                <td className="px-3 py-3 text-right">{totalReceivable > 0 ? fmt(totalReceivable) : '—'}</td>
                <td className="px-3 py-3 text-right">{totalPayable > 0 ? fmt(totalPayable) : '—'}</td>
                <td className="px-3 py-3 text-right text-indigo-300">{fmt(Math.abs(balance))}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Footer summary */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
          <p className="text-xs text-gray-400">Total {rows.length} transactions</p>
          <div className="flex gap-6 text-sm">
            {totalReceivable > 0 && (
              <div className="text-right">
                <p className="text-xs text-gray-500">Total Receivable</p>
                <p className="font-bold text-green-600">{fmt(totalReceivable)}</p>
              </div>
            )}
            {totalPayable > 0 && (
              <div className="text-right">
                <p className="text-xs text-gray-500">Total Payable</p>
                <p className="font-bold text-red-600">{fmt(totalPayable)}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function PartyLedgerPage() {
  const yr = new Date().getFullYear();
  const [search,    setSearch]   = useState('');
  const [contacts,  setContacts] = useState([]);
  const [selected,  setSelected] = useState(null);
  const [start,     setStart]    = useState(`${yr}-04-01`);
  const [end,       setEnd]      = useState(`${yr + 1}-03-31`);
  const [ledger,    setLedger]   = useState(null);
  const [loading,   setLoading]  = useState(false);
  const [loadingC,  setLoadingC] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoadingC(true);
      try {
        const res = await contactsAPI.getAll({ search: search || undefined, limit: 30 });
        setContacts(res.data || []);
      } catch {}
      finally { setLoadingC(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadLedger = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const res = await accountingAPI.getLedgerFull(selected._id, { startDate: start, endDate: end });
      setLedger(res.data);
    } catch { toast.error('Failed to load ledger'); }
    finally { setLoading(false); }
  }, [selected, start, end]);

  useEffect(() => { if (selected) loadLedger(); }, [loadLedger]);

  const handlePrint = () => window.print();

  return (
    <div className="space-y-5">
      <PageHeader title="Party Ledger" subtitle="View complete transaction history — like a party statement" />

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left: Contact selector */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
          <h3 className="font-semibold text-gray-800 text-sm">Select Party</h3>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search customers / vendors..."
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div className="space-y-1.5 max-h-80 overflow-y-auto">
            {loadingC && <p className="text-xs text-gray-400 text-center py-4">Searching...</p>}
            {!loadingC && contacts.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No contacts found</p>}
            {contacts.map(c => (
              <button key={c._id} onClick={() => setSelected(c)}
                className={`w-full text-left px-3 py-2.5 rounded-xl transition-colors border ${selected?._id === c._id ? 'border-indigo-300 bg-indigo-50' : 'border-transparent hover:bg-gray-50'}`}>
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-indigo-600 font-bold text-xs">{(c.name || 'U')[0].toUpperCase()}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{c.name}</p>
                    <p className="text-xs text-gray-400 capitalize">{c.type || 'contact'}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Date range */}
          <div className="border-t border-gray-100 pt-4 space-y-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase">Date Range</h4>
            <div>
              <label className="text-xs text-gray-500 block mb-1">From</label>
              <input type="date" value={start} onChange={e => setStart(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">To</label>
              <input type="date" value={end} onChange={e => setEnd(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { l:'Q1 Apr–Jun', s:`${yr}-04-01`, e:`${yr}-06-30` },
                { l:'Q2 Jul–Sep', s:`${yr}-07-01`, e:`${yr}-09-30` },
                { l:'Q3 Oct–Dec', s:`${yr}-10-01`, e:`${yr}-12-31` },
                { l:'Q4 Jan–Mar', s:`${yr+1}-01-01`, e:`${yr+1}-03-31` },
              ].map(q => (
                <button key={q.l} onClick={() => { setStart(q.s); setEnd(q.e); }}
                  className="px-2 py-1.5 text-[10px] font-medium border border-gray-200 rounded-lg hover:bg-indigo-50 hover:border-indigo-300 text-gray-600 transition-colors">
                  {q.l}
                </button>
              ))}
            </div>
            {selected && (
              <div className="flex gap-2">
                <button onClick={loadLedger} disabled={loading}
                  className="flex-1 py-2 bg-indigo-600 text-white text-xs font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                  {loading ? 'Loading...' : 'Generate Ledger'}
                </button>
                {ledger && (
                  <button onClick={handlePrint} className="p-2 border border-gray-200 rounded-xl hover:bg-gray-50 text-gray-600 transition-colors">
                    <PrinterIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right: Ledger */}
        <div className="lg:col-span-2">
          {!selected && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center h-full flex flex-col items-center justify-center">
              <UserIcon className="w-12 h-12 text-gray-200 mb-3" />
              <p className="text-gray-400 text-sm font-medium">Select a party to view ledger</p>
              <p className="text-gray-300 text-xs mt-1">Search by name, phone, or GSTIN</p>
            </div>
          )}
          {selected && loading && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
              <div className="w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-400">Generating ledger...</p>
            </div>
          )}
          {selected && !loading && ledger && (
            <LedgerTable
              records={ledger.records || []}
              openingBalance={ledger.openingBalance || 0}
              contact={selected}
            />
          )}
        </div>
      </div>
    </div>
  );
}
